<template>
  <div>
    <div class="s-table" v-if="data" :class="tableClass">
      <div class="header row">
        <span class="header col row-number" v-if="showRowNumbers">
          <span class="content">
            <span class="label">#</span>
          </span>
        </span>
        <span class="header col" v-for="(col, idx) in columns" v-bind:key="idx" @click="sortByHandler(idx)">
          <span class="content">
            <span class="label">{{col.header}}</span>
            <span class="control">
              <span class="sort">
                <template v-if="sortByIndex === idx">
                  <span class="icon" v-if="sortReverse">&uarr;</span>
                  <span class="icon" v-else>&darr;</span>
                </template>
              </span>
            </span>
          </span>
        </span>
      </div>
      <div class="row" v-for="(row, idx) in pagedData" v-bind:key="idx" @click="selectRowHandler(idx, row)" @dblclick="doubleClickRowHandler" :class="rowClass(idx)">
        <span class="col row-number" v-if="showRowNumbers">
          <span class="content">
            <span>{{(currentPage * pageSize) + idx + 1}}</span>
          </span>
        </span>
        <span class="col" v-for="(col, idx) in columns" v-bind:key="idx">
          <span class="content">
            <span v-html="cellValue(row, col)"></span>
          </span>
        </span>
      </div>
    </div>
    <div class="footer" v-if="(this.sortedData || []).length > pageSize">
      <button @click="pagePrev" class="btn-left">&lt;</button>
      Page {{(this.currentPage + 1)}} of {{Math.floor((this.sortedData || []).length / pageSize) + 1}}
      <button @click="pageNext" class="btn-right">&gt;</button>
    </div>
  </div>
</template>

<script>
  import SimpleTableHelpers from "./SimpleTableHelpers";
  export default {
    name: "SimpleTable",
    props: [
      "data", "columns", "stretch", "autoSortColumn", "autoSortReverse", "stApi",
      "selectable", "select", "idField", "showRowNumbers",
      "numRows",
    ],
    data : function() {
      return {
        filteredData: null,
        sortedData: null,
        dataIDField: null,
        sortByField: null,
        sortByType: null,
        sortByIndex: null,
        sortReverse: false,
        selectedRowIndex: null,
        selectedRow: null,
        selectedID: null,
        searchString: null,
        currentPage: 0,
        pageSize: 50,
      }
    },
    mounted: function() {
      this.pageSize = this.numRows || this.pageSize
      this.sortReverse = !!this.autoSortReverse
      if (this.autoSortColumn != null) {
        this.sortByIndex = this.autoSortColumn
      }
      this.dataIDField = this.idField || "ID"
      this.filter()
      this.sortAndSelect()
      if (this.stApi) {
        this.stApi.sortBy = this.sortByHandler.bind(this)
        this.stApi.selectById = this.selectById.bind(this)
        this.stApi.filter = this.filter.bind(this)
      }
    },
    watch: {
      data: function() {
        this.filter()
        this.sortAndSelect()
      }
    },
    computed: {
      pagedData: function() {
        return ((this.sortedData || []).concat() || []).slice(this.currentPage * this.pageSize, this.currentPage * this.pageSize + this.pageSize)
      },
      tableClass: function() {
        let classes = []
        if (this.stretch) {
          classes.push('stretch')
        }
        return classes.join(" ")
      },
    },
    methods: {
      pageNext() {
        this.currentPage = Math.min(this.currentPage + 1, Math.floor(this.sortedData.length / this.pageSize))
      },
      pagePrev() {
        this.currentPage = Math.max(this.currentPage - 1, 0)
      },
      rowClass: function(rowIndex) {
        let classes = []
        if (this.selectable === true) {
          classes.push("selectable")
          if (rowIndex + (this.pageSize * this.currentPage) === this.selectedRowIndex) {
            classes.push("selected")
          }
        }
        return classes.join(" ")
      },
      sortAndSelect() {
        if (this.sortByIndex !== null) {
          this.sortByHandler(this.sortByIndex, true)
        } else {
          this.sortedData = (this.filteredData || []).concat();
        }
        this.preselect()
      },
      cellValue(row, col) {
        return SimpleTableHelpers.render(row, col)
      },
      sortByHandler(colIndex, noPreselect) {
        let col = this.columns[colIndex]
        if (this.sortByIndex === colIndex) {
          this.sortReverse = !this.sortReverse;
        } else {
          this.sortReverse = false;
        }
        this.sortByType = col.format;
        this.sortByField = col.field;
        this.sortByIndex = colIndex;

        if (!this.sortByField) {
          this.sortedData = this.filteredData.concat()
        }
        let sorted = (this.filteredData || []).concat().sort(SimpleTableHelpers.sort(col, this.sortReverse))
        this.sortedData = sorted
        if (!noPreselect) {
          this.preselect()
        }
      },
      preselect(fireEvent) {
        // find selected id and preselect
        if (this.selectedID == null) {
          this.selectedRow = null
          this.selectedRowIndex = null
          return
        }
        for (let i = 0; i < this.sortedData.length; i++) {
          let item = this.sortedData[i]
          if (item[this.dataIDField] === this.selectedID) {
            this.selectedRow = item
            this.selectedRowIndex = i
            if (fireEvent) {
              this.$emit('select', this.selectedRow)
            }
            break
          }
        }
      },
      selectById(id) {
        this.selectedID = id
        this.preselect(true)
      },
      selectRowHandler(rowIndex, data) {
        if (this.selectable !== true) {
          return
        }
        if (!data) {
          this.selectedRowIndex = null
          this.selectedRow = null
          this.selectedID = null
          return
        }
        this.selectedRowIndex = rowIndex
        this.selectedRow = data
        this.selectedID = data[this.dataIDField]
        this.$emit('select', data)
      },
      doubleClickRowHandler() {
        this.$emit('dblclick', this.selectedRow)
      },
      filter(search, searchField) {
        if (search != null) {
          this.searchString = search
            this.currentPage = 0;
        }
        let data = (this.data || []).concat()
        if ((this.searchString || "") === "") {
          this.filteredData = data
          if (search != null) {
            this.sortAndSelect()
          }
          return
        }

        let filtered = []
        for (let i = 0; i < data.length; i++) {
          let item = data[i]
          let searchFieldValue = item[searchField || this.dataIDField] || ""
          if (searchFieldValue.indexOf(this.searchString) > -1) {
            filtered.push(item)
          }
        }
        this.filteredData = filtered
        if (search != null) {
          this.sortAndSelect()
        }
      }
    }
  }

</script>

<style scoped lang="less">
  .s-table {
    display: table;
    border-collapse: collapse;
    cursor: default;
    font-size: 10pt;

    .row {
      display: table-row;
      border-bottom: 1px solid #F0F0F0;
      &.header {
        background: #FAFAFA;
        user-select: none;
        padding: 2pt;
        .label {
          font-weight: bold;
        }
        .control {
          float: right;
        }
      }
      .col {
        display: table-cell;
        white-space: nowrap;
        padding: 5pt 0pt;

        /*border: 1px solid #E0E0E0;*/
        .content {
          display: block;
          border-left: 1px solid #F0F0F0;
          padding: 0pt 10pt;
        }
        &:last-child {
          .content {
            border-right: 1px solid #E0E0E0;
          }
        }
        &.row-number {
          background: #FAFAFA;
          text-align: center;
          color: #CCCCCC;
          &.header {
            visibility: hidden;
          }
          .content {
            border-color: transparent;
            text-align: right;
          }
        }
      }
      &.selected {
        background: rgba(0, 191, 255, 0.15);
      }

      &.selectable{
        &:hover {
          background: rgba(0, 191, 255, 0.08);
        }
      }
    }
    &.stretch {
      width: 100%;
    }
  }
  .footer {
    text-align: center;
    padding: 4pt 6pt;
    font-size: 10pt;
    height: 24pt;
    box-sizing: border-box;
    background: #FAFAFA;
    border-bottom: 1px solid #F0F0F0;
    .btn-left {
      float: left;
    }
    .btn-right {
      float: right;
    }
  }

</style>
